// Attachments list for issue detail — with image thumbnail previews
import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Paperclip, Download, FileText, Image, X } from 'lucide-react'
import { supabase } from '@/lib/supabase'

interface Attachment {
  id: string
  file_name: string
  file_type: string
  file_size_bytes: number
  storage_path: string
  storage_bucket: string
  created_at: string
}

interface IssueAttachmentsListProps {
  attachments: Attachment[]
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isImageFile(fileType: string): boolean {
  return fileType.startsWith('image/')
}

function getFileIcon(fileType: string) {
  if (isImageFile(fileType)) return <Image className="h-4 w-4 text-kaycoo-blue" />
  return <FileText className="h-4 w-4 text-kaycoo-charcoal-2" />
}

function ImageThumbnail({ attachment }: { attachment: Attachment }) {
  const [url, setUrl] = useState<string | null>(null)
  const [fullView, setFullView] = useState(false)

  useEffect(() => {
    supabase.storage
      .from(attachment.storage_bucket)
      .createSignedUrl(attachment.storage_path, 3600)
      .then(({ data }) => { if (data?.signedUrl) setUrl(data.signedUrl) })
  }, [attachment.storage_path, attachment.storage_bucket])

  if (!url) return null

  return (
    <>
      <img src={url} alt={attachment.file_name}
        className="mt-1 h-16 w-16 cursor-pointer rounded border border-kaycoo-white-3 object-cover"
        onClick={() => setFullView(true)} />
      {fullView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => setFullView(false)}>
          <div className="relative max-h-[90vh] max-w-[90vw]">
            <Button variant="ghost" size="icon" onClick={() => setFullView(false)}
              className="absolute -right-2 -top-2 rounded-full bg-white shadow">
              <X className="h-4 w-4" />
            </Button>
            <img src={url} alt={attachment.file_name} className="max-h-[85vh] max-w-[85vw] rounded-lg" />
          </div>
        </div>
      )}
    </>
  )
}

export function IssueAttachmentsList({ attachments }: IssueAttachmentsListProps): JSX.Element {
  const handleDownload = async (attachment: Attachment) => {
    const { data } = await supabase.storage
      .from(attachment.storage_bucket)
      .createSignedUrl(attachment.storage_path, 3600)

    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank')
    }
  }

  if (attachments.length === 0) {
    return (
      <Card className="border-kaycoo-teal-light bg-kaycoo-white">
        <CardContent className="py-4 text-center text-sm text-kaycoo-charcoal-2">
          No attachments
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-kaycoo-teal-light bg-kaycoo-white">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm text-kaycoo-teal-dark">
          <Paperclip className="h-4 w-4" />
          Attachments ({attachments.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {attachments.map((att) => (
          <div key={att.id}
            className="rounded-md border border-kaycoo-white-3 p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 overflow-hidden">
                {getFileIcon(att.file_type)}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-kaycoo-charcoal">{att.file_name}</p>
                  <p className="text-xs text-kaycoo-charcoal-2">{formatFileSize(att.file_size_bytes)}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleDownload(att)}
                className="text-kaycoo-teal-dark hover:text-kaycoo-teal-dark-2">
                <Download className="h-4 w-4" />
              </Button>
            </div>
            {isImageFile(att.file_type) && <ImageThumbnail attachment={att} />}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
